import type { BabelFileResult, Node, NodePath, TransformOptions } from "@babel/core";
import type { ParserOptions } from "@babel/parser";
import type { Scope, TraverseOptions } from "@babel/traverse";
import type { File } from "@babel/types";

//
// A bundle-size-friendly deferred-loading Babel wrapper using @babel/standalone.
// @babel/standalone is browser-compatible (no Node.js fs dependency).
//
// Babel is loaded when the page is imported. API is async-only because loading is deferred.
//

// Cached instance of @babel/standalone
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
type BabelStandalone = typeof import("@babel/standalone");
let babelStandalone: BabelStandalone | null = null;

const getBabel = async () => {
	if (!babelStandalone) {
		babelStandalone = await import("@babel/standalone");
	}
	return babelStandalone;
};

/**
 * Options for parseAsync - accepts both parser options and a plugins array
 */
export interface ParseOptions {
	parserOpts?: ParserOptions;
	plugins?: Array<string | [string, Record<string, unknown>]>;
}

/**
 * Parse code into an AST using @babel/standalone's parser
 */
export const parseAsync = async (code: string, options?: ParseOptions): Promise<File | null> => {
	const babel = await getBabel();
	// Merge parserOpts with plugins
	const parserOptions: ParserOptions = {
		...options?.parserOpts,
		plugins: options?.plugins as ParserOptions["plugins"],
	};
	// Use any cast to work around @babel/types version conflicts

	return babel.packages.parser.parse(code, parserOptions) as any;
};

export const traverseAsync = async (
	parent: Node | null,
	opts?: TraverseOptions<Node>,
	scope?: Scope | undefined,
	state?: Node,
	parentPath?: NodePath
): Promise<void> => {
	const babel = await getBabel();
	// @babel/standalone exposes traverse through packages.traverse.default
	// Use any cast to work around type version conflicts between @babel/types versions
	const traverse = babel.packages.traverse.default;

	return traverse(parent as any, opts as any, scope as any, state as any, parentPath as any);
};

export const makeTraverseSync = async () => {
	const babel = await getBabel();
	return babel.packages.traverse.default;
};

/**
 * Transform an AST to JavaScript code using @babel/standalone
 * Uses presets instead of plugins for transformation
 */
export const transformFromAstAsync = async (
	ast: Node,
	code?: string,
	opts?: TransformOptions
): Promise<BabelFileResult | null> => {
	const babel = await getBabel();
	// @babel/standalone's transformFromAst expects presets, not plugins
	// Convert plugins to presets for standalone
	const transformOpts: TransformOptions = {
		...opts,
		// Use presets for transformation in @babel/standalone
		presets: opts?.presets || [["typescript", { isTSX: true, allExtensions: true }], "react"],
	};

	return babel.transformFromAst(
		ast as any,
		code,
		transformOpts
	) as unknown as BabelFileResult | null;
};
